/**
 * Utility functions for cross-tab communication between admin tabs
 * to coordinate impersonation cleanup during logout
 */

const CHANNEL_NAME = 'admin-impersonation-status'
const CHECK_MESSAGE = 'CHECK_BEFORE_SIGNOUT'
const ACTIVE_MESSAGE = 'IMPERSONATION_ACTIVE'
const RESPONSE_TIMEOUT_MS = 200

/**
 * Used by admin tab (not impersonating) to check if there are
 * active impersonations in other tabs before signing out
 */
export const checkForActiveImpersonations = (): Promise<boolean> =>
  new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    let hasActiveImpersonations = false

    // Listen for responses from impersonating tabs
    channel.onmessage = (event) => {
      if (event.data.type === ACTIVE_MESSAGE) {
        hasActiveImpersonations = true
        clearTimeout(responseTimeout)
        channel.close()
        resolve(true)
      }
    }

    // Set timeout - if no response within timeout, assume no active impersonations
    const responseTimeout = setTimeout(() => {
      channel.close()
      resolve(hasActiveImpersonations)
    }, RESPONSE_TIMEOUT_MS)

    // Broadcast the check
    channel.postMessage({ type: CHECK_MESSAGE })
  })

/**
 * Used by admin tabs that are impersonating to respond to
 * signout checks from the main admin tab
 */
export const setupImpersonationResponder = (): (() => void) => {
  const channel = new BroadcastChannel(CHANNEL_NAME)

  // Listen for check messages and respond if impersonating
  channel.onmessage = (event) => {
    if (event.data.type === CHECK_MESSAGE) {
      channel.postMessage({ type: ACTIVE_MESSAGE })
    }
  }

  // Return cleanup function
  return () => {
    channel.close()
  }
}
